/**
 * recenterBoneBind — the descriptor half of unbind-adjust-rebind (the
 * micro-adjustment class Allen named 2026-07-28; first instance: the WRIST,
 * which generated rigs place in FRONT of the wrist flesh — measured variance,
 * universal across the Tripo corpus). ONE responsibility: move a bone's BIND
 * POSITION to a new world point while the mesh stays exactly where it is.
 *
 * Why that is possible without touching a single weight: the rest state
 * composes boneWorld · IBM per bone, which is the identity at bind by
 * construction. Move the bone's rest AND recompute its inverse-bind together
 * and the identity is preserved — the mesh does not move; only the ROTATION
 * PIVOT does, which is the entire point (a hand rotating about a point in
 * front of the wrist orbits instead of hinges). Ratified in
 * HAND_STUDIO_KNUCKLES.md §settled: "moving a bone's rest position AND its
 * inverse-bind matrix together leaves the mesh exactly where it is."
 *
 * The descriptor's own laws hold: worldBind is the IBM-derived truth, so it
 * takes the new position with rotation/scale untouched; localBind is the
 * exact decomposition against the parent's worldBind (the graft-digits
 * idiom, parent scale divided out); CHILDREN keep their world binds, so
 * their localBind re-decomposes against the moved parent. The result is
 * re-gated by parseRigDescriptor — never a hand-patched descriptor.
 *
 * Fail-loud: an unknown bone, or a parent the descriptor lacks, throws — a
 * caller bug, not a product outcome.
 *
 * Depends on: ir/rig-descriptor, math/quat, math/vec3.
 * Depended on by: app session driver (run-document, wristRecenter).
 */
import { KernelError } from '../ir/errors.js';
import { parseRigDescriptor, type RigBone, type RigDescriptor } from '../ir/rig-descriptor.js';
import { quatInvertUnit, quatRotateVec3 } from '../math/quat.js';
import type { Vec3Data } from '../math/vec3.js';

export class RecenterError extends KernelError {}

/** Decompose a world position into `parent`-local space (scale divided out — the graft idiom). */
function toLocal(parent: RigBone, world: Vec3Data): Vec3Data {
  const inv = quatInvertUnit(parent.worldBind.rotation);
  const d = quatRotateVec3(inv, {
    x: world.x - parent.worldBind.position.x,
    y: world.y - parent.worldBind.position.y,
    z: world.z - parent.worldBind.position.z,
  });
  const s = parent.worldBind.scale;
  return { x: d.x / s.x, y: d.y / s.y, z: d.z / s.z };
}

/**
 * Return a new descriptor with `boneName`'s bind moved to `newWorldPosition`
 * (rotation and scale untouched), children's world binds preserved.
 */
export function recenterBoneBind(
  rig: RigDescriptor,
  boneName: string,
  newWorldPosition: Vec3Data,
): RigDescriptor {
  const bone = rig.bones.find((b) => b.name === boneName);
  if (bone === undefined) {
    throw new RecenterError(`cannot recenter "${boneName}" — no such bone in the descriptor.`);
  }
  const moved: RigBone = {
    ...bone,
    worldBind: { ...bone.worldBind, position: { ...newWorldPosition } },
    localBind:
      bone.parent === null
        ? { ...bone.localBind, position: { ...newWorldPosition } }
        : (() => {
            const parent = rig.bones.find((b) => b.name === bone.parent);
            if (parent === undefined) {
              throw new RecenterError(
                `cannot recenter "${boneName}" — its parent "${bone.parent}" is not in the descriptor.`,
              );
            }
            return { ...bone.localBind, position: toLocal(parent, newWorldPosition) };
          })(),
  };
  const bones = rig.bones.map((b) => {
    if (b.name === boneName) return moved;
    // Children keep their WORLD binds; their local decomposition re-derives
    // against the moved parent.
    if (b.parent === boneName) {
      return { ...b, localBind: { ...b.localBind, position: toLocal(moved, b.worldBind.position) } };
    }
    return b;
  });
  return parseRigDescriptor({ ...rig, bones });
}
